import { useState, useCallback, useEffect, useRef } from 'react'
import { api } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Modal,
  ColumnSettingsButton,
  colors,
  input,
  btnSecondary,
  btnPrimary,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

type Container = 'api' | 'frontend' | 'hl7' | 'sync' | '*'
type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'

interface LogEntry {
  readonly timestamp: string
  readonly container: string
  readonly level: string
  readonly requestId?: string
  readonly message: string
  readonly raw: Record<string, unknown>
}

interface LogLevelData {
  readonly currentLevel: string
  readonly defaultLevel: string
  readonly resetAt: string | null
}

interface LogsApiResponse {
  readonly data?: readonly LogEntry[]
  readonly meta?: { readonly nextCursor?: string }
  readonly warnings?: readonly string[]
}

interface Filters {
  readonly container: Container
  readonly level: LogLevel | ''
  readonly since: string
  readonly until: string
  readonly requestId: string
}

const CONTAINERS: ReadonlyArray<{ value: Container; label: string }> = [
  { value: '*', label: 'All' },
  { value: 'api', label: 'API' },
  { value: 'frontend', label: 'Frontend' },
  { value: 'hl7', label: 'HL7' },
  { value: 'sync', label: 'Sync' },
]

const LOG_LEVELS: ReadonlyArray<LogLevel> = ['trace', 'debug', 'info', 'warn', 'error', 'fatal']

const QUICK_RANGES: ReadonlyArray<{ label: string; minutes: number }> = [
  { label: 'Last 15m', minutes: 15 },
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 24h', minutes: 1440 },
]

const LEVEL_COLORS: Readonly<Record<string, { bg: string; color: string }>> = {
  error: { bg: '#fee2e2', color: '#dc2626' },
  fatal: { bg: '#fce7f3', color: '#9d174d' },
  warn: { bg: '#fef3c7', color: '#92400e' },
  info: { bg: '#dbeafe', color: '#1e40af' },
  debug: { bg: '#f1f5f9', color: '#64748b' },
  trace: { bg: '#f0fdf4', color: '#166534' },
}

function levelBadgeStyle(level: string): React.CSSProperties {
  const { bg, color } = LEVEL_COLORS[level] ?? { bg: '#f1f5f9', color: '#64748b' }
  return {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: bg,
    color,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  }
}

const containerBadge: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.15rem 0.5rem',
  borderRadius: '3px',
  fontSize: '0.75rem',
  fontWeight: 600,
  background: '#e0e7ff',
  color: '#3730a3',
  whiteSpace: 'nowrap',
}

export function AdminLogsPage() {
  const [filters, setFilters] = useState<Filters>({
    container: '*',
    level: '',
    since: '',
    until: '',
    requestId: '',
  })
  const [logs, setLogs] = useState<readonly LogEntry[]>([])
  const [nextCursor, setNextCursor] = useState<string | undefined>()
  const [warnings, setWarnings] = useState<readonly string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logLevelData, setLogLevelData] = useState<LogLevelData | null>(null)
  const [tailMode, setTailMode] = useState(false)
  const [expandedEntry, setExpandedEntry] = useState<LogEntry | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('admin-logs')

  // Stable ref to latest logs for tail polling (avoids re-creating the interval
  // on every log update while still reading the most recent timestamp)
  const logsRef = useRef<readonly LogEntry[]>([])
  useEffect(() => {
    logsRef.current = logs
  }, [logs])

  const buildParams = useCallback(
    (extra?: { cursor?: string; since?: string }) => {
      const params = new URLSearchParams({ limit: '100' })
      if (filters.container !== '*') params.set('container', filters.container)
      if (filters.level) params.set('level', filters.level)
      const since = extra?.since ?? filters.since
      if (since) params.set('since', since)
      if (filters.until) params.set('until', filters.until)
      if (filters.requestId) params.set('requestId', filters.requestId)
      if (extra?.cursor) params.set('cursor', extra.cursor)
      return params
    },
    [filters],
  )

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = (await api.get<readonly LogEntry[]>(`/admin/logs?${buildParams()}`)) as unknown as LogsApiResponse
      setLogs(result.data ?? [])
      setNextCursor(result.meta?.nextCursor)
      setWarnings(result.warnings ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }, [buildParams])

  const loadOlder = useCallback(async () => {
    if (!nextCursor) return
    setLoading(true)
    try {
      const result = (await api.get<readonly LogEntry[]>(
        `/admin/logs?${buildParams({ cursor: nextCursor })}`,
      )) as unknown as LogsApiResponse
      setLogs((prev) => [...prev, ...(result.data ?? [])])
      setNextCursor(result.meta?.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load older logs')
    } finally {
      setLoading(false)
    }
  }, [buildParams, nextCursor])

  const tailFetch = useCallback(async () => {
    const since = logsRef.current.length > 0 ? logsRef.current[0].timestamp : undefined
    try {
      const result = (await api.get<readonly LogEntry[]>(
        `/admin/logs?${buildParams({ since })}`,
      )) as unknown as LogsApiResponse
      const newEntries = result.data ?? []
      if (newEntries.length > 0) {
        setLogs((prev) => [...newEntries, ...prev])
      }
    } catch {
      // tail poll errors are silent — the banner covers persistent outages
    }
  }, [buildParams])

  // Fetch on filter change
  useEffect(() => {
    void fetchLogs()
  }, [fetchLogs])

  // Fetch log level once on mount
  useEffect(() => {
    void api.get<LogLevelData>('/admin/log-level').then((res) => setLogLevelData(res.data ?? null))
  }, [])

  // Tail mode 2 s interval
  useEffect(() => {
    if (!tailMode) return
    const id = setInterval(() => void tailFetch(), 2000)
    return () => clearInterval(id)
  }, [tailMode, tailFetch])

  const changeLogLevel = useCallback(async (level: LogLevel) => {
    try {
      const res = await api.post<LogLevelData>('/admin/log-level', { level, durationSec: 300 })
      setLogLevelData(res.data ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change log level')
    }
  }, [])

  function applyQuickRange(minutes: number) {
    const since = new Date(Date.now() - minutes * 60 * 1000).toISOString().slice(0, 16)
    setFilters((f) => ({ ...f, since, until: '' }))
  }

  const hasJournaldWarning = warnings.some((w) => w.startsWith('log_reader_not_available'))

  const columns: readonly Column<LogEntry>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      width: '185px',
      render: (e) => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: colors.textMuted }}>
          {new Date(e.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'container',
      header: 'Container',
      width: '100px',
      render: (e) => <span style={containerBadge}>{e.container}</span>,
    },
    {
      key: 'level',
      header: 'Level',
      width: '80px',
      render: (e) => <span style={levelBadgeStyle(e.level)}>{e.level}</span>,
    },
    {
      key: 'requestId',
      header: 'Request ID',
      width: '150px',
      render: (e) =>
        e.requestId ? (
          <button
            type="button"
            data-testid={`req-id-btn-${e.requestId}`}
            onClick={(ev) => {
              ev.stopPropagation()
              setFilters((f) => ({ ...f, requestId: e.requestId! }))
            }}
            title="Filter by this Request ID"
            style={{
              background: 'none',
              border: 'none',
              color: colors.accent,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontFamily: 'monospace',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {e.requestId.length > 10 ? `${e.requestId.slice(0, 8)}…` : e.requestId}
          </button>
        ) : (
          <span style={{ color: colors.textMuted }}>—</span>
        ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (e) => (
        <span
          style={{
            display: 'block',
            maxWidth: '500px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.875rem',
          }}
          title={e.message}
        >
          {e.message}
        </span>
      ),
    },
  ]

  const logLevelControl = logLevelData ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Log level:</span>
      <span data-testid="log-level-badge" style={levelBadgeStyle(logLevelData.currentLevel)}>
        {logLevelData.currentLevel}
      </span>
      <select
        data-testid="log-level-select"
        value={logLevelData.currentLevel}
        onChange={(e) => void changeLogLevel(e.target.value as LogLevel)}
        style={{ ...input, width: 'auto', height: '32px', padding: '0 0.5rem', fontSize: '0.8125rem' }}
      >
        {LOG_LEVELS.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
      {logLevelData.resetAt && (
        <span data-testid="log-level-reset-hint" style={{ fontSize: '0.75rem', color: colors.textMuted }}>
          resets at {new Date(logLevelData.resetAt).toLocaleTimeString()}
        </span>
      )}
    </div>
  ) : null

  const tailButton = (
    <button
      type="button"
      data-testid="tail-toggle"
      onClick={() => setTailMode((m) => !m)}
      style={{
        ...(tailMode ? btnPrimary : btnSecondary),
        padding: '0.3rem 0.75rem',
        fontSize: '0.8125rem',
      }}
    >
      {tailMode ? 'Tail: ON' : 'Tail: OFF'}
    </button>
  )

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <ColumnSettingsButton columns={columns} {...tableColumnProps} />
      {logLevelControl}
      {tailButton}
    </div>
  )

  return (
    <div>
      <PageHeader title="System Logs" actions={headerActions} />

      {hasJournaldWarning && (
        <div
          data-testid="journald-warning-banner"
          style={{
            padding: '0.6rem 1rem',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '4px',
            marginBottom: '0.75rem',
            fontSize: '0.875rem',
            color: '#1e40af',
          }}
        >
          Note: journald is not available on this host. Log retrieval may be limited or unavailable. The filter bar and
          log-level control are still active.
        </div>
      )}

      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
        }}
      >
        {/* Container chips */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {CONTAINERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              data-testid={`container-chip-${value}`}
              onClick={() => setFilters((f) => ({ ...f, container: value }))}
              style={{
                padding: '0.2rem 0.6rem',
                borderRadius: '9999px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${filters.container === value ? colors.accent : colors.border}`,
                background: filters.container === value ? colors.accent : 'transparent',
                color: filters.container === value ? '#fff' : colors.textMuted,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Level filter */}
        <select
          data-testid="level-filter"
          value={filters.level}
          onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value as LogLevel | '' }))}
          style={{ ...input, width: '120px', height: '32px', padding: '0 0.5rem', fontSize: '0.8125rem' }}
        >
          <option value="">All levels</option>
          {LOG_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>

        {/* Quick range buttons */}
        {QUICK_RANGES.map(({ label, minutes }) => (
          <button
            key={minutes}
            type="button"
            data-testid={`quick-range-${minutes}`}
            onClick={() => applyQuickRange(minutes)}
            style={{
              padding: '0.2rem 0.6rem',
              borderRadius: '3px',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.textMuted,
            }}
          >
            {label}
          </button>
        ))}

        {/* Time range inputs */}
        <input
          data-testid="since-input"
          type="datetime-local"
          value={filters.since}
          onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
          style={{ ...input, width: '185px', height: '32px', padding: '0 0.5rem', fontSize: '0.8125rem' }}
        />
        <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>to</span>
        <input
          data-testid="until-input"
          type="datetime-local"
          value={filters.until}
          onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))}
          style={{ ...input, width: '185px', height: '32px', padding: '0 0.5rem', fontSize: '0.8125rem' }}
        />

        {/* Request ID search */}
        <input
          data-testid="request-id-input"
          type="text"
          placeholder="Request ID..."
          value={filters.requestId}
          onChange={(e) => setFilters((f) => ({ ...f, requestId: e.target.value }))}
          style={{ ...input, width: '160px', height: '32px', padding: '0 0.5rem', fontSize: '0.8125rem' }}
        />

        {/* Clear all filters */}
        {(filters.container !== '*' || filters.level || filters.since || filters.until || filters.requestId) && (
          <button
            type="button"
            onClick={() => setFilters({ container: '*', level: '', since: '', until: '', requestId: '' })}
            style={{
              background: 'none',
              border: 'none',
              color: colors.accent,
              cursor: 'pointer',
              fontSize: '0.8125rem',
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '0.6rem 1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '4px',
            marginBottom: '0.75rem',
            color: '#dc2626',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Log table */}
      {loading && logs.length === 0 ? (
        <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={logs}
            keyField="timestamp"
            onRowClick={setExpandedEntry}
            emptyMessage="No log entries found."
            {...tableColumnProps}
          />
          {!tailMode && nextCursor && (
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <button
                type="button"
                data-testid="load-older-btn"
                onClick={() => void loadOlder()}
                disabled={loading}
                style={{ ...btnSecondary, fontSize: '0.875rem', padding: '0.4rem 1rem' }}
              >
                {loading ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Row expand modal */}
      <Modal
        isOpen={expandedEntry !== null}
        onClose={() => setExpandedEntry(null)}
        title="Log Entry Detail"
        width="640px"
      >
        {expandedEntry && (
          <div>
            <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={levelBadgeStyle(expandedEntry.level)}>{expandedEntry.level}</span>
              <span style={containerBadge}>{expandedEntry.container}</span>
              <span style={{ fontSize: '0.8125rem', color: colors.textMuted, fontFamily: 'monospace' }}>
                {new Date(expandedEntry.timestamp).toLocaleString()}
              </span>
            </div>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', color: colors.text }}>{expandedEntry.message}</p>
            <pre
              data-testid="raw-json"
              style={{
                background: colors.bgMuted,
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                padding: '0.75rem',
                fontSize: '0.8125rem',
                fontFamily: 'monospace',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '400px',
                overflow: 'auto',
                margin: 0,
              }}
            >
              {JSON.stringify(expandedEntry.raw, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  )
}
